import { useCallback, useRef } from 'react'

/**
 * Custom hook that tracks pointer travel between mousedown and mouseup on a grid cell.
 * Triggers onToggle when pointer movement is below the threshold (< 6px), preventing
 * race conditions and duplicate toggles between native click and mouseup events.
 *
 * @param {Function} onToggle - Callback executed to toggle the cell's selection state
 * @returns {{ onMouseDown: Function, onMouseUp: Function, onClick: Function }} Event handlers for the cell element
 */
export function useCellClick(onToggle) {
  const downPosRef = useRef(null)
  const handledOnMouseUpRef = useRef(false)

  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      handledOnMouseUpRef.current = false
      downPosRef.current = { x: e.clientX, y: e.clientY }
    }
  }, [])

  const handleMouseUp = useCallback((e) => {
    if (e.button === 0 && downPosRef.current) {
      const dist = Math.hypot(e.clientX - downPosRef.current.x, e.clientY - downPosRef.current.y)
      downPosRef.current = null
      if (dist < 6) {
        handledOnMouseUpRef.current = true
        if (onToggle) {
          onToggle()
        }
      }
    }
  }, [onToggle])

  const handleClick = useCallback(() => {
    if (handledOnMouseUpRef.current) {
      handledOnMouseUpRef.current = false
      return
    }
    if (onToggle) {
      onToggle()
    }
  }, [onToggle])

  return {
    onMouseDown: handleMouseDown,
    onMouseUp:   handleMouseUp,
    onClick:     handleClick,
  }
}
